"use client";

import { Capacitor } from "@capacitor/core";
import { mapConcurrent } from "@/lib/media/map-concurrent";
import {
  DevicePermissionError,
  isPermissionDeniedError,
} from "@/lib/native/permission-guidance";
import { assertNativeMediaSize, nativeMediaLimit } from "./media-import-limits";

export type PickedChatMedia = {
  file: File;
  kind: "image" | "video";
};

export const isNativePickerAvailable = () => Capacitor.isNativePlatform();

function base64ToBytes(base64: string): Uint8Array {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

/**
 * Turn a native file reference into a File the existing presigned-upload
 * pipeline can PUT.
 *
 * Order matters: `fetch(convertFileSrc(path))` works on Android (the webview
 * intercepts `_capacitor_file_` even with a remote server.url) but is a known
 * failure on iOS remote-URL setups — there we fall back to a Filesystem
 * base64 read.
 */
async function fileFromNativeRef({
  path,
  webPath,
  name,
  mimeType,
}: {
  path?: string | null;
  webPath?: string | null;
  name: string;
  mimeType: string;
}): Promise<File | null> {
  const fetchTarget = webPath ?? (path ? Capacitor.convertFileSrc(path) : null);

  if (fetchTarget) {
    try {
      const response = await fetch(fetchTarget);
      if (response.ok) {
        const length = Number(response.headers.get("content-length"));
        if (length > nativeMediaLimit(mimeType))
          throw new Error("Selected media is too large");
        const blob = await response.blob();
        assertNativeMediaSize(blob.size, mimeType);
        return new File([blob], name, { type: mimeType || blob.type });
      }
    } catch {
      // Fall through to the Filesystem read.
    }
  }

  if (path) {
    try {
      const { Filesystem } = await import("@capacitor/filesystem");
      const info = await Filesystem.stat({ path });
      assertNativeMediaSize(info.size, mimeType);
      const result = await Filesystem.readFile({ path });
      if (typeof result.data === "string") {
        const bytes = base64ToBytes(result.data);
        return new File([new Blob([bytes as BlobPart])], name, {
          type: mimeType,
        });
      }
      return new File([result.data], name, { type: mimeType });
    } catch {
      return null;
    }
  }

  return null;
}

/**
 * Native gallery picker — PHPicker on iOS, Photo Picker on Android. Both are
 * permissionless system UIs (no READ_MEDIA_* / photo-library prompts).
 */
export async function pickChatMedia({
  limit = 10,
  onReady,
}: {
  limit?: number;
  onReady?: (items: PickedChatMedia[]) => void;
} = {}): Promise<PickedChatMedia[]> {
  const { FilePicker } = await import("@capawesome/capacitor-file-picker");

  let result: Awaited<ReturnType<typeof FilePicker.pickMedia>>;
  try {
    result = await FilePicker.pickMedia({ limit, readData: false });
  } catch (error) {
    const message =
      error instanceof Error
        ? error.message
        : typeof error === "object" && error !== null && "message" in error
          ? String(error.message)
          : "";
    // iOS rejects rather than returning an empty list when Cancel is tapped.
    // No selection is an ordinary outcome; real permission/read errors escape.
    if (/^pick(?:Files|Media|Images) cancel(?:ed|led)\.?$/i.test(message))
      return [];
    throw error;
  }

  // Validate the entire selection before allocating any blobs/base64 buffers.
  let totalBytes = 0;
  for (const entry of result.files) {
    let size = entry.size;
    if (!size && entry.path) {
      const { Filesystem } = await import("@capacitor/filesystem");
      size = (await Filesystem.stat({ path: entry.path })).size;
    }
    totalBytes = assertNativeMediaSize(size, entry.mimeType ?? "", totalBytes);
  }
  const ready = new Map<number, PickedChatMedia>();
  let nextReady = 0;
  const picked = await mapConcurrent(result.files, 2, async (entry, index) => {
    const mimeType = entry.mimeType ?? "";
    const kind: PickedChatMedia["kind"] = mimeType.startsWith("video/")
      ? "video"
      : "image";
    const file = await fileFromNativeRef({
      path: entry.path,
      name: entry.name || `media-${Date.now()}`,
      mimeType,
    });
    if (!file)
      throw new Error(
        "Could not read selected media. Try downloading it to your device first.",
      );
    assertNativeMediaSize(file.size, mimeType);
    const media = { file, kind };
    ready.set(index, media);
    const batch: PickedChatMedia[] = [];
    while (ready.has(nextReady)) {
      const item = ready.get(nextReady);
      if (item) batch.push(item);
      ready.delete(nextReady++);
    }
    if (batch.length) onReady?.(batch);
    return media;
  });

  return picked;
}

/**
 * Native gallery picker for the feed post composer. Returns plain Files for the
 * shared presigned-upload pipeline (kind is re-derived during upload).
 */
export async function pickPostMedia({
  limit = 6,
}: {
  limit?: number;
} = {}): Promise<File[]> {
  const picked = await pickChatMedia({ limit });
  return picked.map((item) => item.file);
}

/** Capture a photo with the system camera (chat composer / view-once). */
export async function captureChatPhoto(): Promise<PickedChatMedia | null> {
  const { Camera, CameraResultType, CameraSource } = await import(
    "@capacitor/camera"
  );

  const permission = await Camera.requestPermissions({
    permissions: ["camera"],
  });
  if (permission.camera !== "granted") {
    throw new DevicePermissionError(
      "camera",
      "Camera access is turned off in system settings",
    );
  }

  let photo: Awaited<ReturnType<typeof Camera.getPhoto>>;
  try {
    photo = await Camera.getPhoto({
      resultType: CameraResultType.Uri,
      source: CameraSource.Camera,
      quality: 90,
      saveToGallery: false,
      correctOrientation: true,
    });
  } catch (error) {
    if (isPermissionDeniedError(error)) {
      throw new DevicePermissionError(
        "camera",
        error instanceof Error
          ? error.message
          : "Camera access is turned off in system settings",
      );
    }
    throw error;
  }

  const file = await fileFromNativeRef({
    path: photo.path,
    webPath: photo.webPath,
    name: `photo-${Date.now()}.${photo.format || "jpeg"}`,
    mimeType: `image/${photo.format || "jpeg"}`,
  });

  return file ? { file, kind: "image" } : null;
}
